// Performance utilities for the theme

// Intersection Observer for lazy loading
export const createLazyLoader = (selector = "[data-lazy]", options = {}) => {
  const defaultOptions = {
    rootMargin: "50px",
    threshold: 0.1,
    ...options,
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const element = entry.target;

        // Handle images
        if (element.tagName === "IMG" && element.dataset.src) {
          element.src = element.dataset.src;
          element.classList.add("loaded");
        }

        // Handle background images
        if (element.dataset.bg) {
          element.style.backgroundImage = `url(${element.dataset.bg})`;
        }

        // Handle custom lazy loading
        if (element.dataset.lazy) {
          element.classList.add("lazy-loaded");

          // Trigger custom event
          element.dispatchEvent(new CustomEvent("lazyload"));
        }

        observer.unobserve(element);
      }
    });
  }, defaultOptions);

  // Observe all matching elements
  document.querySelectorAll(selector).forEach((element) => {
    observer.observe(element);
  });

  return observer;
};

// Debounce utility for performance
export const debounce = (func, wait, immediate) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
};

// Throttle utility
export const throttle = (func, limit) => {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// Performance monitoring
export const performanceMonitor = {
  // Mark performance timing
  mark: (name) => {
    if (performance.mark) {
      performance.mark(name);
    }
  },

  // Measure between marks
  measure: (name, startMark, endMark) => {
    if (performance.measure) {
      performance.measure(name, startMark, endMark);
    }
  },

  // Get navigation timing
  getPageLoadTime: () => {
    return performance.timing.loadEventEnd - performance.timing.navigationStart;
  },

  // Get first contentful paint
  getFCP: () => {
    return new Promise((resolve) => {
      new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.name === "first-contentful-paint") {
            resolve(entry.startTime);
          }
        }
      }).observe({ entryTypes: ["paint"] });
    });
  },
};

// Preload critical resources
export const preloadResource = (href, as, type = null) => {
  const link = document.createElement("link");
  link.rel = "preload";
  link.href = href;
  link.as = as;
  if (type) link.type = type;
  document.head.appendChild(link);
};

// Initialize lazy loading on DOM ready
document.addEventListener("DOMContentLoaded", () => {
  createLazyLoader();
});
